import Head from 'next/head';

/**
 * Provide SEO related meta tags to a page.
 *
 * @param {Props} props The props object.
 * @param {string} props.title Used for the page title, og:title, twitter:title, etc.
 * @param {string} props.description Used for the meta description, og:description, twitter:description, etc.
 * @param {string} props.imageUrl Used for the og:image and twitter:image. NOTE: Must be an absolute url.
 * @param {string} props.url Used for the og:url and twitter:url.
 * @param {string} [props.type] 'website' | 'article'. Default 'website'.
 * @param {string} [props.publishedTime] ISO 8601 date for article:published_time.
 * @param {string} [props.modifiedTime] ISO 8601 date for article:modified_time.
 * @param {Array<{hreflang: string, href: string}>} [props.alternates] Hreflang alternate links.
 *
 * @returns {React.ReactElement} The SEO component
 */
export default function SEO({ title, description, imageUrl, url, type = 'website', publishedTime, modifiedTime, alternates }) {
  if (!title && !description && !imageUrl && !url) {
    return null;
  }

  const ogType = type === 'article' ? 'article' : 'website';

  return (
    <>
      <Head>
        <meta property="og:type" content={ogType} />
        <meta property="twitter:card" content="summary_large_image" />
        {ogType === 'article' && publishedTime && (
          <meta property="article:published_time" content={publishedTime} />
        )}
        {ogType === 'article' && modifiedTime && (
          <meta property="article:modified_time" content={modifiedTime} />
        )}

        {title && (
          <>
            <title>{title}</title>
            <meta name="title" content={title} />
            <meta property="og:title" content={title} />
            <meta property="twitter:title" content={title} />
          </>
        )}

        {description && (
          <>
            <meta name="description" content={description} />
            <meta property="og:description" content={description} />
            <meta property="twitter:description" content={description} />
          </>
        )}

        {imageUrl && (
          <>
            <meta property="og:image" content={imageUrl} />
            <meta property="twitter:image" content={imageUrl} />
          </>
        )}

        {url && (
          <>
            <link rel="canonical" href={url} />
            <meta property="og:url" content={url} />
            <meta property="twitter:url" content={url} />
          </>
        )}

        {alternates?.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
        ))}
      </Head>
    </>
  );
}
